export * from "./types.js";
export class ApiError extends Error {
    status;
    code;
    payload;
    constructor(message, status, code, payload) {
        super(message);
        this.status = status;
        this.code = code;
        this.payload = payload;
    }
    /** Sunucu "bu iş taze TOTP onayı ister" dediğinde true. */
    get needsTotp() {
        return this.code === "TOTP_REQUIRED";
    }
}
/**
 * Web ve mobil konsolun ortak istemcisi. Tek sorumluluğu HTTP + WS; UI kararı yok.
 *
 * Access token kısa ömürlüdür (15 dk); 401 alındığında bir kez refresh denenir ve
 * istek tekrarlanır. İkinci 401'de oturum düşürülür.
 */
export class GwrControlClient {
    baseUrl;
    tokens;
    onUnauthorized;
    constructor(baseUrl, tokens, onUnauthorized) {
        this.baseUrl = baseUrl;
        this.tokens = tokens;
        this.onUnauthorized = onUnauthorized;
    }
    // --- HTTP ---------------------------------------------------------------
    async request(path, init = {}, retry = true) {
        const access = this.tokens.getAccess();
        const res = await fetch(`${this.baseUrl}${path}`, {
            ...init,
            headers: {
                // Gövdesiz POST'ta (totp/setup, logout) bu başlık varsa Fastify 400 döner:
                // FST_ERR_CTP_EMPTY_JSON_BODY.
                ...(init.body != null ? { "Content-Type": "application/json" } : {}),
                ...(access ? { Authorization: `Bearer ${access}` } : {}),
                ...init.headers,
            },
        });
        if (res.status === 401 && retry && this.tokens.getRefresh()) {
            const refreshed = await this.refresh();
            if (refreshed)
                return this.request(path, init, false);
            this.tokens.clear();
            this.onUnauthorized?.();
        }
        const text = await res.text();
        let payload = null;
        try {
            payload = text ? JSON.parse(text) : null;
        }
        catch {
            // nginx 502/504 HTML sayfası: "Unexpected token <" yerine anlamlı bir hata.
            throw new ApiError(`HTTP ${res.status}`, res.ok ? 502 : res.status);
        }
        if (!res.ok) {
            const body = payload;
            throw new ApiError(body?.error ?? `HTTP ${res.status}`, res.status, body?.code, payload);
        }
        return payload;
    }
    async refresh() {
        const refreshToken = this.tokens.getRefresh();
        if (!refreshToken)
            return false;
        try {
            const res = await fetch(`${this.baseUrl}/auth/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refreshToken }),
            });
            if (!res.ok)
                return false;
            const data = (await res.json());
            this.tokens.set({ accessToken: data.accessToken });
            return true;
        }
        catch {
            return false;
        }
    }
    // --- Kimlik -------------------------------------------------------------
    async login(input) {
        const res = await fetch(`${this.baseUrl}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        const payload = (await res.json());
        if (!res.ok)
            throw new ApiError(payload.error ?? "Giriş başarısız", res.status, payload.code);
        this.tokens.set({ accessToken: payload.accessToken, refreshToken: payload.refreshToken });
        return payload;
    }
    me() {
        return this.request("/auth/me");
    }
    logout() {
        return this.request("/auth/logout", { method: "POST" });
    }
    totpSetup() {
        return this.request("/auth/totp/setup", { method: "POST" });
    }
    totpEnable(totp) {
        return this.request("/auth/totp/enable", { method: "POST", body: JSON.stringify({ totp }) });
    }
    /** Mevcut parola ya da anlık TOTP koduyla parola değiştirir; diğer oturumlar kapanır. */
    changePassword(input) {
        return this.request("/auth/password", { method: "POST", body: JSON.stringify(input) });
    }
    /** Yıkıcı işlemden hemen önce: TOTP doğrulanır, taze damgalı token alınır. */
    async confirm(totp) {
        const res = await this.request("/auth/confirm", {
            method: "POST",
            body: JSON.stringify({ totp }),
        });
        this.tokens.set({ accessToken: res.accessToken });
    }
    // --- Uygulamalar & agent'lar -------------------------------------------
    async apps() {
        const res = await this.request("/apps");
        return res.apps;
    }
    app(id) {
        return this.request(`/apps/${id}`);
    }
    async agents() {
        const res = await this.request("/agents");
        return res.agents;
    }
    async audit() {
        const res = await this.request("/audit");
        return res.entries;
    }
    // --- İşler --------------------------------------------------------------
    createJob(input) {
        return this.request("/jobs", {
            method: "POST",
            body: JSON.stringify({ params: {}, ...input }),
        });
    }
    async jobs(query = {}) {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query))
            if (v !== undefined)
                params.set(k, String(v));
        const res = await this.request(`/jobs?${params}`);
        return res.jobs;
    }
    job(id) {
        return this.request(`/jobs/${id}`);
    }
    async jobLogs(id, after = -1) {
        const res = await this.request(`/jobs/${id}/logs?after=${after}`);
        return res.logs;
    }
    cancelJob(id) {
        return this.request(`/jobs/${id}/cancel`, { method: "POST" });
    }
    // --- Bildirimler --------------------------------------------------------
    pushEstimate(appId, target) {
        return this.request("/push/estimate", {
            method: "POST",
            body: JSON.stringify({ appId, target }),
        });
    }
    pushSend(input) {
        return this.request("/push/send", { method: "POST", body: JSON.stringify(input) });
    }
    async pushCampaigns(appId) {
        const query = appId ? `?appId=${encodeURIComponent(appId)}` : "";
        const res = await this.request(`/push/campaigns${query}`);
        return res.campaigns;
    }
    async deviceStats(appId) {
        const res = await this.request(`/push/devices/stats?appId=${encodeURIComponent(appId)}`);
        return res.breakdown;
    }
    // --- Feature flag -------------------------------------------------------
    async flags(appId) {
        const query = appId ? `?appId=${encodeURIComponent(appId)}` : "";
        const res = await this.request(`/flags${query}`);
        return res.flags;
    }
    setFlag(appId, key, value) {
        return this.request(`/flags/${appId}/${encodeURIComponent(key)}`, {
            method: "PUT",
            body: JSON.stringify({ value }),
        });
    }
    deleteFlag(appId, key) {
        return this.request(`/flags/${appId}/${encodeURIComponent(key)}`, { method: "DELETE" });
    }
    // --- Kullanım analitiği -------------------------------------------------
    async analyticsSummary() {
        const res = await this.request("/analytics");
        return res.apps;
    }
    analytics(appId, days = 30) {
        return this.request(`/analytics/${encodeURIComponent(appId)}?days=${days}`);
    }
    analyticsProgress(appId) {
        return this.request(`/analytics/${encodeURIComponent(appId)}/progress`);
    }
    // --- Yayın hataları -----------------------------------------------------
    async streamFailures(appId, status = "open") {
        const res = await this.request(`/stream-failures?appId=${encodeURIComponent(appId)}&status=${status}`);
        return res.failures;
    }
    resolveStreamFailure(appId, url, resolved = true) {
        return this.request("/stream-failures/resolve", {
            method: "POST",
            body: JSON.stringify({ appId, url, resolved }),
        });
    }
    // --- İstasyon şikayetleri -----------------------------------------------
    async stationReports(appId, status = "open") {
        const res = await this.request(`/station-reports?appId=${encodeURIComponent(appId)}&status=${status}`);
        return res.reports;
    }
    resolveStationReport(appId, stationId, reason, resolved = true) {
        return this.request("/station-reports/resolve", {
            method: "POST",
            body: JSON.stringify({ appId, stationId, reason, resolved }),
        });
    }
    // --- İstasyon başvuruları -----------------------------------------------
    stationSubmissions(appId, status = "pending") {
        return this.request(`/station-submissions?appId=${encodeURIComponent(appId)}&status=${status}`);
    }
    recheckStationSubmission(id, streamUrl) {
        return this.request(`/station-submissions/${id}/recheck`, {
            method: "POST",
            body: JSON.stringify({ streamUrl }),
        });
    }
    approveStationSubmission(id, input) {
        return this.request(`/station-submissions/${id}/approve`, {
            method: "POST",
            body: JSON.stringify(input),
        });
    }
    rejectStationSubmission(id, note) {
        return this.request(`/station-submissions/${id}/reject`, {
            method: "POST",
            body: JSON.stringify({ note }),
        });
    }
    // --- Oyuncu geri bildirimi ----------------------------------------------
    feedbackCounts() {
        return this.request("/feedback-admin/counts");
    }
    feedbackPosts(appId, tab = "pending", sort = "new") {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts?tab=${tab}&sort=${sort}`);
    }
    feedbackPost(appId, id) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${id}`);
    }
    approveFeedback(appId, id, status = "open") {
        return this.feedbackAction(appId, id, "approve", { status });
    }
    rejectFeedback(appId, id, note) {
        return this.feedbackAction(appId, id, "reject", { note });
    }
    setFeedbackStatus(appId, id, status) {
        return this.feedbackAction(appId, id, "status", { status });
    }
    /** Oyuncunun seçtiği türü düzelt. */
    setFeedbackCategory(appId, id, category) {
        return this.feedbackAction(appId, id, "category", { category });
    }
    /** Herkese açık cevap (panoda görünür). */
    replyFeedback(appId, id, text) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${id}/reply`, {
            method: "PUT",
            body: JSON.stringify({ text }),
        });
    }
    deleteFeedbackReply(appId, id) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${id}/reply`, { method: "DELETE" });
    }
    /** Yazara özel mesaj (yazışma). */
    messageFeedback(appId, id, text) {
        return this.feedbackAction(appId, id, "messages", { text });
    }
    resolveFeedbackReports(appId, id) {
        return this.feedbackAction(appId, id, "reports/resolve", {});
    }
    /** Geri alınamaz; taze TOTP ister (ApiError.needsTotp). */
    deleteFeedback(appId, id) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${id}`, { method: "DELETE" });
    }
    blockFeedbackAuthor(appId, authorId, input = {}) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/authors/${encodeURIComponent(authorId)}/block`, { method: "POST", body: JSON.stringify(input) });
    }
    unblockFeedbackAuthor(appId, authorId) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/authors/${encodeURIComponent(authorId)}/unblock`, { method: "POST", body: JSON.stringify({}) });
    }
    // --- Geri bildirim yorumları ---
    /** Gönderinin bütün yorumları (gizli ve kaldırılmış dahil), eskiden yeniye. */
    feedbackComments(appId, postId) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${postId}/comments`);
    }
    /** Bildirilen ya da gizlenen (kaldırılmamış) yorumlar. */
    flaggedFeedbackComments(appId) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/comments?filter=flagged`);
    }
    /** Geliştirici yorumu (fromDev): panoda hemen görünür. */
    devFeedbackComment(appId, postId, text) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${postId}/comments`, {
            method: "POST",
            body: JSON.stringify({ text }),
        });
    }
    removeFeedbackComment(appId, commentId) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/comments/${commentId}/remove`, {
            method: "POST",
            body: JSON.stringify({}),
        });
    }
    /** Gizlenen/kaldırılan yorumu geri açar; açık bildirimleri kapatır. */
    restoreFeedbackComment(appId, commentId) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/comments/${commentId}/restore`, {
            method: "POST",
            body: JSON.stringify({}),
        });
    }
    feedbackAction(appId, id, action, body) {
        return this.request(`/feedback-admin/${encodeURIComponent(appId)}/posts/${id}/${action}`, {
            method: "POST",
            body: JSON.stringify(body),
        });
    }
    // --- İstasyon dizini ---------------------------------------------------------
    stations(params) {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params))
            if (v !== undefined && v !== "")
                qs.set(k, String(v));
        return this.request(`/stations?${qs.toString()}`);
    }
    stationState(country) {
        return this.request(`/stations/state${country ? `?country=${encodeURIComponent(country)}` : ""}`);
    }
    station(id) {
        return this.request(`/stations/${encodeURIComponent(id)}`);
    }
    updateStation(id, patch) {
        return this.request(`/stations/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
        });
    }
    /** url verilirse ve kayıtlıdan farklıysa yalnızca dener, hiçbir şey yazmaz. */
    checkStation(id, url) {
        return this.request(`/stations/${encodeURIComponent(id)}/check`, {
            method: "POST",
            body: JSON.stringify(url ? { url } : {}),
        });
    }
    /** Bu istasyon için yeni yayın adresi arar (en fazla ~70 sn). */
    findStationSources(id) {
        return this.request(`/stations/${encodeURIComponent(id)}/find`, { method: "POST", body: "{}" });
    }
    /**
     * Toplu kaynak arama (arka planda). groupKeys verilirse yalnızca o istasyonlar,
     * yoksa ülkenin çalışan kaynağı olmayanları. İlerleme stationState().sync.finder'da.
     */
    findSources(params = {}) {
        return this.request("/stations/find", { method: "POST", body: JSON.stringify(params) });
    }
    /** Uygulamada olmayan TR istasyonlarını uygulamanın TR listesine ekler. */
    addStationsToApp(groupKeys) {
        return this.request("/stations/add-to-app", { method: "POST", body: JSON.stringify({ groupKeys }) });
    }
    /** Yasaklanan istasyon kuralları (Raadiocast blockedStations flag'i). */
    stationBans() {
        return this.request("/stations/bans");
    }
    /** Ad (tüm kaynakları), yayın sunucusu ya da tek kayıt kimliğiyle yasaklar. */
    banStations(kind, values, note) {
        return this.request("/stations/bans", { method: "POST", body: JSON.stringify({ kind, values, note }) });
    }
    unbanStations(kind, values) {
        return this.request("/stations/bans/remove", { method: "POST", body: JSON.stringify({ kind, values }) });
    }
    /** Toplu aramayı eldeki istasyon bitince durdurur. */
    stopFindSources() {
        return this.request("/stations/find/stop", { method: "POST", body: "{}" });
    }
    /** Kaynağı siler (işaretler; gece senkronu ya da bulucu geri getirmez). */
    deleteStation(id) {
        return this.request(`/stations/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    mirrorStation(id) {
        return this.request(`/stations/${encodeURIComponent(id)}/mirror`, { method: "POST", body: "{}" });
    }
    /** Tek istasyon: logosu rakip sunucusundaysa kendi sitesinden logo ara (öneri olarak). */
    findStationLogo(id) {
        return this.request(`/stations/${encodeURIComponent(id)}/find-logo`, { method: "POST", body: "{}" });
    }
    decideLogoProposal(id, accept) {
        return this.request(`/stations/${encodeURIComponent(id)}/logo-proposal`, { method: "POST", body: JSON.stringify({ accept }) });
    }
    /** Kaynaksız istasyon: "kontrol edildi, kaynak bulunamadı" (dead) ya da geri al. */
    markStationNoSource(groupKey, dead) {
        return this.request("/stations/group-dead", { method: "POST", body: JSON.stringify({ groupKey, dead }) });
    }
    /** radio-polska'dan gelen istasyonu uygulamaya al ya da kalıcı düşür. */
    reviewExternalStation(groupKey, approve) {
        return this.request("/stations/external-review", { method: "POST", body: JSON.stringify({ groupKey, approve }) });
    }
    stationDuplicates(params) {
        const qs = new URLSearchParams();
        if (params.country)
            qs.set("country", params.country);
        if (params.q)
            qs.set("q", params.q);
        if (params.dismissed)
            qs.set("dismissed", "1");
        if (params.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params.offset !== undefined)
            qs.set("offset", String(params.offset));
        return this.request(`/stations/duplicates?${qs.toString()}`);
    }
    /** Seçilen istasyonları (grupları) tek istasyon yapar: kaynaklarına aynı elle grup. */
    mergeStationDuplicates(groupKeys) {
        return this.request("/stations/duplicates/merge", { method: "POST", body: JSON.stringify({ groupKeys }) });
    }
    /** Aynı kaynaklı olası kopyaları onaysız birleştirir (arka planda; durum stationState().sync.dupAutoMerge). */
    autoMergeStationDuplicates() {
        return this.request("/stations/duplicates/auto-merge", { method: "POST", body: "{}" });
    }
    /** Chunklist adreslerini ana listeye çevirir (arka planda; durum stationState().sync.chunklistClean). */
    cleanStationChunklists() {
        return this.request("/stations/chunklist-clean", { method: "POST", body: "{}" });
    }
    /** Kümeyi "ayrı kalsın" diye işaretler (dismissed=false geri alır). */
    dismissStationDuplicates(groupKeys, dismissed) {
        return this.request("/stations/duplicates/dismiss", { method: "POST", body: JSON.stringify({ groupKeys, dismissed }) });
    }
    startLogoFinder(opts) {
        return this.request("/stations/logo-finder", { method: "POST", body: JSON.stringify(opts) });
    }
    stopLogoFinder() {
        return this.request("/stations/logo-finder/stop", { method: "POST", body: "{}" });
    }
    syncStations(what) {
        return this.request("/stations/sync", { method: "POST", body: JSON.stringify({ what }) });
    }
    // --- Moderasyon ---------------------------------------------------------
    moderationSchema() {
        return this.request("/moderation/schema");
    }
    moderationReports(kind = "title") {
        return this.request(`/moderation/reports?kind=${kind}`);
    }
    /** Kenar çubuğu rozeti için açık şikâyet sayıları. */
    moderationCounts() {
        return this.request("/moderation/counts");
    }
    moderationSearch(q, kind = "title") {
        return this.request(`/moderation/search?kind=${kind}&q=${encodeURIComponent(q)}`);
    }
    moderationUser(userId) {
        return this.request(`/moderation/users/${userId}`);
    }
    warnUser(userId, input) {
        return this.request(`/moderation/users/${userId}/warn`, {
            method: "POST",
            body: JSON.stringify(input),
        });
    }
    /** days: 0 = kalıcı. TOTP ister. */
    suspendUser(userId, input) {
        return this.request(`/moderation/users/${userId}/suspend`, {
            method: "POST",
            body: JSON.stringify(input),
        });
    }
    unsuspendUser(userId) {
        return this.request(`/moderation/users/${userId}/suspend`, { method: "DELETE" });
    }
    /** hidden=false ile aynı uç toplu gizlemeyi geri alır. */
    hideAllComments(userId, hidden = true) {
        return this.request(`/moderation/users/${userId}/hide-all`, {
            method: "POST",
            body: JSON.stringify({ hidden }),
        });
    }
    async suspensions() {
        const res = await this.request("/moderation/suspensions");
        return res.suspensions;
    }
    async moderationLog(limit = 60) {
        const res = await this.request(`/moderation/log?limit=${limit}`);
        return res.actions;
    }
    setCommentVisibility(id, hidden, kind = "title") {
        return this.request(`/moderation/comments/${id}/visibility`, {
            method: "POST",
            body: JSON.stringify({ kind, hidden }),
        });
    }
    dismissReport(id, kind = "title") {
        return this.request(`/moderation/comments/${id}/dismiss?kind=${kind}`, { method: "POST" });
    }
    deleteComment(id, kind = "title") {
        return this.request(`/moderation/comments/${id}?kind=${kind}`, { method: "DELETE" });
    }
    // --- Moderasyon ayarları ve kuralları -----------------------------------
    async moderationSettings() {
        const res = await this.request("/moderation/settings");
        return res.settings;
    }
    setModerationSetting(key, value) {
        return this.request(`/moderation/settings/${encodeURIComponent(key)}`, {
            method: "PUT",
            body: JSON.stringify({ value }),
        });
    }
    async blocklist() {
        const res = await this.request("/moderation/blocklist");
        return res.rules;
    }
    addBlockRule(input) {
        return this.request("/moderation/blocklist", {
            method: "POST",
            body: JSON.stringify(input),
        });
    }
    toggleBlockRule(id, enabled) {
        return this.request(`/moderation/blocklist/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ enabled }),
        });
    }
    deleteBlockRule(id) {
        return this.request(`/moderation/blocklist/${id}`, { method: "DELETE" });
    }
    /** Toplu gizleme / şikâyet kapatma. Toplu silme bilerek yok. */
    bulkModeration(input) {
        return this.request("/moderation/bulk", { method: "POST", body: JSON.stringify(input) });
    }
    // --- Canlı olay akışı ---------------------------------------------------
    /**
     * Olay soketi salt okunurdur; komut kabul etmez. Tarayıcı WebSocket'i özel başlık
     * gönderemediği için token query'den geçer — bu uç yalnızca okuma yaptığı için
     * kabul edilebilir bir taviz.
     */
    /**
     * ⚠ Token her bağlantıda YENİDEN okunur: access token 15 dakikada ölüyor. Bir kez
     * okunup saklanırsa ilk kopmadan sonraki her deneme ölü token'la yapılır ve akış
     * bir daha hiç bağlanmaz (panelde "olay akışı: kopuk"). Sunucu 4401 ile kapatırsa
     * önce token yenilenir.
     */
    connectEvents(onEvent, onStatus) {
        const wsUrl = this.baseUrl.replace(/^http/, "ws");
        let socket = null;
        let closed = false;
        let delay = 1000;
        let timer;
        const open = () => {
            if (closed)
                return;
            const token = this.tokens.getAccess();
            socket = new WebSocket(`${wsUrl}/events?token=${encodeURIComponent(token ?? "")}`);
            socket.onopen = () => {
                delay = 1000;
                onStatus?.(true);
            };
            socket.onmessage = (msg) => {
                try {
                    onEvent(JSON.parse(msg.data));
                }
                catch {
                    /* bozuk mesaj yok sayılır */
                }
            };
            socket.onclose = (ev) => {
                onStatus?.(false);
                if (closed)
                    return;
                if (ev.code === 4401) {
                    // Token süresi dolmuş: yenile, olmuyorsa oturum düşmüştür.
                    void this.refresh().then((ok) => {
                        if (closed)
                            return;
                        if (!ok) {
                            this.tokens.clear();
                            this.onUnauthorized?.();
                            return;
                        }
                        timer = setTimeout(open, 250);
                    });
                    return;
                }
                timer = setTimeout(open, delay);
                delay = Math.min(delay * 2, 30_000);
            };
        };
        open();
        return () => {
            closed = true;
            if (timer)
                clearTimeout(timer);
            socket?.close();
        };
    }
}
//# sourceMappingURL=index.js.map